import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { FormProvider } from 'react-hook-form';
import {
  debounce,
  deepCopy,
  INPUT_DEBOUNCE_DELAY,
  SUBMIT_MODE,
} from 'src/util';
import PropTypes from 'prop-types';
import { isObjectEmpty } from '../loanOfficer/helpers';

const AutosavingFormContext = createContext({});

/**
 * @param {{
 *   onSave: (data: any) => Promise<void>,
 *   defaultValues: {},
 *   ready: boolean,
 *   formMethods: import('react-hook-form').UseFormReturn,
 *   children: import('react').ReactNode,
 *   mode: 'onChange' | 'onIsValid',
 *   disabled?: boolean
 * }} props
 * @returns
 */
export const AutosavingFormProvider = ({
  onSave,
  onError,
  defaultValues,
  ready,
  formMethods,
  children,
  mode,
  disabled,
  saveDependencies,
  id,
}) => {
  const {
    reset,
    handleSubmit,
    formState: { errors, dirtyFields },
    watch,
  } = formMethods;
  const formValues = watch();
  const initialized = useRef(false);
  const firstMount = useRef(true);
  const formValueString = JSON.stringify(formValues);
  const dirtyFieldsString = JSON.stringify(dirtyFields ?? {});
  const errorsString = JSON.stringify(errors ?? {});
  const errorsRef = useRef(errorsString);
  errorsRef.current = errorsString;

  useEffect(() => {
    if (ready && !initialized.current) {
      initialized.current = ready;
      reset(defaultValues || {});
    }
  }, [ready, defaultValues, reset]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const saveData = useCallback(onSave, saveDependencies);

  // Store refs for values that need to be accessed in debounced function
  const saveDataRef = useRef(saveData);
  const modeRef = useRef(mode);
  const handleSubmitRef = useRef(handleSubmit);
  const onErrorRef = useRef(onError);
  const disabledRef = useRef(disabled);
  const resetRef = useRef(reset);

  // Update refs when values change
  saveDataRef.current = saveData;
  modeRef.current = mode;
  handleSubmitRef.current = handleSubmit;
  onErrorRef.current = onError;
  disabledRef.current = disabled;
  resetRef.current = reset;

  // Create debounced function once and keep it stable
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const triggerChange = useMemo(
    () =>
      debounce(({ data, dirtyFields }) => {
        if (!disabledRef.current) {
          const errors = JSON.parse(errorsRef.current);

          const dataCopy = deepCopy(data);
          if (modeRef.current === SUBMIT_MODE.onChange) {
            saveDataRef.current(
              dataCopy,
              getOnlyDirtyValuesFromData(dataCopy, dirtyFields),
            );
            resetRef.current(data, { keepErrors: true, keepValues: true });
          } else if (modeRef.current === SUBMIT_MODE.onIsPartialValid) {
            if (isObjectEmpty(errors)) {
              saveDataRef.current(
                dataCopy,
                getOnlyDirtyValuesFromData(dataCopy, dirtyFields),
              );
            } else {
              const validData = getPartialValidData(dataCopy, errors);
              const dirtyValues = getOnlyDirtyValuesFromData(
                validData,
                dirtyFields,
              );
              if (!isObjectEmpty(dirtyValues)) {
                saveDataRef.current(validData, dirtyValues);
              }
            }
            resetRef.current(data, { keepErrors: true, keepValues: true });
          } else {
            handleSubmitRef.current(
              (data) =>
                saveDataRef.current(
                  data,
                  getOnlyDirtyValuesFromData(data, dirtyFields),
                ),
              onErrorRef.current,
            )();
          }
        }
      }, INPUT_DEBOUNCE_DELAY),
    [],
  );

  useEffect(() => {
    if (firstMount.current) {
      firstMount.current = false;
      return;
    }
    const dirtyFields = JSON.parse(dirtyFieldsString);
    if (Object.keys(dirtyFields).length) {
      triggerChange({
        data: JSON.parse(formValueString),
        dirtyFields,
      });
    }
  }, [dirtyFieldsString, formValueString, triggerChange]);

  const providerValue = useMemo(
    () => ({
      triggerChange,
      id,
    }),
    [triggerChange, id],
  );

  return (
    <AutosavingFormContext.Provider value={providerValue}>
      <FormProvider {...formMethods}>{children}</FormProvider>
    </AutosavingFormContext.Provider>
  );
};

AutosavingFormProvider.defaultProps = {
  onSave: () => null,
  onError: () => null,
  mode: SUBMIT_MODE.onChange,
  saveDependencies: [],
};

AutosavingFormProvider.propTypes = {
  mode: PropTypes.oneOf([
    SUBMIT_MODE.onChange,
    SUBMIT_MODE.onIsValid,
    SUBMIT_MODE.onIsPartialValid,
  ]),
  disabled: PropTypes.bool,
};

export const useAutosavingFormContext = () => {
  return useContext(AutosavingFormContext);
};

const getPartialValidData = (data, errors) => {
  const result = {};
  for (const key in data) {
    const value = data[key];
    if (!errors[key] || value === '') {
      result[key] = value;
    } else if (errors[key]) {
      if (errors[key].type && errors[key].ref) {
        // this is an error, and the value is not ""
        // so it cannot be saved
      } else if (value && typeof value === 'object') {
        // this is a nested error
        result[key] = getPartialValidData(value, errors[key]);
      }
    }
  }
  return result;
};

const getOnlyDirtyValuesFromData = (data, dirtyFields) => {
  const res = {};
  for (const k in data) {
    if (dirtyFields[k] === true) {
      res[k] = data[k];
    } else if (dirtyFields[k] !== null && typeof dirtyFields[k] === 'object') {
      const innerDirty = getOnlyDirtyValuesFromData(data[k], dirtyFields[k]);
      if (innerDirty && Object.keys(innerDirty).length) {
        res[k] = innerDirty;
      }
    }
  }
  return res;
};

AutosavingFormProvider.propTypes = {
  onSave: PropTypes.func.isRequired,
  onError: PropTypes.func,
  defaultValues: PropTypes.object,
  ready: PropTypes.bool,
  formMethods: PropTypes.shape({
    reset: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    formState: PropTypes.shape({
      errors: PropTypes.object,
      dirtyFields: PropTypes.object,
    }).isRequired,
    watch: PropTypes.func.isRequired,
  }).isRequired,
  children: PropTypes.node,
  mode: PropTypes.oneOf(['onChange', 'onIsValid']),
  disabled: PropTypes.bool,
  saveDependencies: PropTypes.array,
  id: PropTypes.string,
};
